import useLogbogApi from "../../../core/hooks/useLogbogApi";
import { subscribeForPushNotifications, unsubscribeFromPushNotifications } from "../pushNotificationSubscriber";
import { useEffect, useState } from "react";

type PushNotificationSubscriptionState = {
  /** Holds permission-state for push-notifications (in browser) */
  notificationPermission?: NotificationPermission;
  /** Whether current device (browser) has a subscription */
  deviceHasSubscription?: boolean;
  /** Whether a subscription exists on backend for user (mathcing deviceSubscription) */
  backendHasSubscription?: boolean;
};

const initialState: PushNotificationSubscriptionState = {
  notificationPermission: undefined,
  deviceHasSubscription: undefined,
  backendHasSubscription: undefined,
};

export const usePushNotificationSubscription = () => {
  const { logbogPushNotificationApi } = useLogbogApi();
  const [subscriptionState, setSubscriptionState] = useState<PushNotificationSubscriptionState>(initialState);

  useEffect(() => {
    const setupSubscriptionState = async () => {
      const registration = await navigator.serviceWorker.getRegistration();
      const subcription = await registration?.pushManager.getSubscription();

      /** Solves that multiple users log-on to the same pushDevice or device was removed from backend */ 
      const backendHasSubscriptionOnCurrentUser = subcription ? await logbogPushNotificationApi.hasSubscription(subcription) : false;

      setSubscriptionState({
        notificationPermission: Notification.permission,
        deviceHasSubscription: !!subcription,
        backendHasSubscription: backendHasSubscriptionOnCurrentUser,
      });
    }
    setupSubscriptionState();
  }, [logbogPushNotificationApi, setSubscriptionState]);

  const requestNotificationPermission = async () => {
    const permission = await window.Notification.requestPermission();

    if (permission !== "granted") {
      setSubscriptionState((prev) => ({ 
        ...prev, 
        notificationPermission: permission
      }));
    }

    return permission;
  };

  const subscribe = async (registration: ServiceWorkerRegistration) => {
    const subscription = await subscribeForPushNotifications(logbogPushNotificationApi, registration);
    const hasSubscription = !!subscription;

    setSubscriptionState({ 
      notificationPermission: Notification.permission,
      deviceHasSubscription: hasSubscription,
      backendHasSubscription: hasSubscription
    });
  }

  const unsubscribe = async (registration: ServiceWorkerRegistration) => {
    await unsubscribeFromPushNotifications(logbogPushNotificationApi, registration);

    setSubscriptionState({ 
      notificationPermission: Notification.permission, 
      //Always set false, even though unsubscribe fails.
      deviceHasSubscription: false, 
      backendHasSubscription: false,
    });
  }

  return {
    subscriptionState,
    requestNotificationPermission,
    subscribe,
    unsubscribe,
  }
}